import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { useAppDispatch } from '@store/hooks';

import {
  isQueueRunSettled,
  queueRunApi,
  type QueueRunApiState,
  type QueueRunArgs,
  selectQueueRun,
} from '../../../store';
import type { QueueViewKey } from '../../../types/encounterWork';
import type { QueueRun } from '../../../utils/queueRun';

import { HOME_QUEUES } from './homeQueues';

/**
 * Home's queue runs (GDN-3379): one per queue per visit, read ahead.
 *
 * On mount the five queues are read into the run cache in card order, one
 * walk at a time, so the network carries one queue's pages rather than five
 * queues' at once and the first card's run is whole before the last card's
 * starts. A click takes the run its queue already has — the first page is
 * usually in hand before the biller has chosen a row — and a run older than
 * a minute at the click is read again, because the queue has moved on.
 *
 * `initiate`, not a hook: the reads are driven by a mount and by clicks, not
 * by a render. They stay SUBSCRIBED while Home is mounted so the walks keep
 * their entries; on unmount every subscription is released and each entry
 * lives out `keepUnusedDataFor`, long enough for the workbench the click
 * opened to subscribe to the one it needs.
 */

/** A run older than this at the click is read again: the queue has moved on. */
export const QUEUE_RUN_MAX_AGE_MS = 60_000;

export interface HomeQueueRunHandle {
  runId: string;
  /** The run as the cache holds it now, at least its first page — or null
   *  while that page is still on the wire. */
  loaded: QueueRun | null;
  /** The run once its first page is in: the same promise every click on
   *  this queue joins. Rejects when that page failed after its retries. */
  firstPage: Promise<QueueRun>;
}

export interface HomeQueueRuns {
  open: (queue: QueueViewKey) => HomeQueueRunHandle;
}

/**
 * The documented shape of a query's `initiate()` result, as much of it as
 * this needs. The app tsconfig types dispatch without the injected-endpoint
 * thunk overload (see `useMedicalRecordsExport`), so the result is asserted
 * rather than inferred.
 */
interface QueueRunSubscription {
  unsubscribe(): void;
  unwrap(): Promise<QueueRun>;
}

export interface RunStore {
  getState: () => unknown;
  subscribe: (listener: () => void) => () => void;
}

const nonce = () => `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 8)}`;

/**
 * Resolves once the run will grow no further without a retry, or once the
 * signal aborts. Either way the store listener is released: a walk cut short
 * by Home unmounting never settles, and a listener waiting for it would
 * otherwise outlive the page.
 */
export function whenQueueRunSettled(
  store: RunStore,
  args: QueueRunArgs,
  signal?: AbortSignal,
): Promise<void> {
  const settled = () =>
    isQueueRunSettled(selectQueueRun(args)(store.getState() as QueueRunApiState));
  return new Promise(resolve => {
    if (settled() || signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      unsubscribe();
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const unsubscribe = store.subscribe(() => {
      if (settled()) done();
    });
    signal?.addEventListener('abort', done, { once: true });
  });
}

export function useHomeQueueRuns(): HomeQueueRuns {
  const dispatch = useAppDispatch();
  const store = useStore();
  const issued = useRef(new Map<QueueViewKey, { at: number; runId: string }>());
  const subscriptions = useRef<QueueRunSubscription[]>([]);

  const runIdFor = useCallback((queue: QueueViewKey): string => {
    const current = issued.current.get(queue);
    const now = Date.now();
    if (current && now - current.at < QUEUE_RUN_MAX_AGE_MS) return current.runId;
    const runId = `${queue}:${nonce()}`;
    issued.current.set(queue, { at: now, runId });
    return runId;
  }, []);

  const open = useCallback(
    (queue: QueueViewKey): HomeQueueRunHandle => {
      const args: QueueRunArgs = { queue, runId: runIdFor(queue) };
      const loaded = selectQueueRun(args)(store.getState() as QueueRunApiState).data ?? null;
      const subscription = dispatch(
        queueRunApi.endpoints.getQueueRun.initiate(args),
      ) as unknown as QueueRunSubscription;
      subscriptions.current.push(subscription);
      const firstPage = subscription.unwrap();
      /* The preload opens runs nobody awaits. A first page that fails is
         reported by the click that needs it, not as an unhandled rejection. */
      firstPage.catch(() => undefined);
      return { firstPage, loaded, runId: args.runId };
    },
    [dispatch, runIdFor, store],
  );

  useEffect(() => {
    const leaving = new AbortController();
    void (async () => {
      for (const { key } of HOME_QUEUES) {
        if (leaving.signal.aborted) return;
        const { runId } = open(key);
        await whenQueueRunSettled(store, { queue: key, runId }, leaving.signal);
      }
    })();
    return () => {
      leaving.abort();
      subscriptions.current.forEach(subscription => subscription.unsubscribe());
      subscriptions.current = [];
    };
  }, [open, store]);

  return { open };
}
